import { useState } from 'react';
import { Box, Typography, Button, TextField, Alert, Divider } from '@mui/material';
import { Add as AddIcon, Warning as WarningIcon } from '@mui/icons-material';

interface AddRowProps {
  addEmail: string;
  setAddEmail: (v: string) => void;
  addLoading: boolean;
  addError: string | null;
  addWarning: string | null;
  agencyDomain: string | null;
  onAdd: () => void;
}

const AddEmailRow = ({
  addEmail, setAddEmail, addLoading, addError, addWarning, agencyDomain, onAdd,
}: AddRowProps) => {
  const [domainWarning, setDomainWarning] = useState<string | null>(null);

  const handleEmailChange = (val: string) => {
    setAddEmail(val);
    if (!agencyDomain || !val.includes('@')) { setDomainWarning(null); return; }
    const typedDomain = val.split('@')[1]?.toLowerCase();
    if (typedDomain && typedDomain !== agencyDomain) {
      setDomainWarning(`Domain doesn't match the agency domain (@${agencyDomain})`);
    } else {
      setDomainWarning(null);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      {addWarning && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          {addWarning}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Add reporter email"
          value={addEmail}
          onChange={e => handleEmailChange(e.target.value)}
          error={!!addError}
          disabled={addLoading}
          onKeyDown={e => e.key === 'Enter' && !addLoading && addEmail && onAdd()}
          sx={{ flex: 1 }}
          style={{ marginBottom: 0 }}
          InputLabelProps={{ sx: { '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 12px) scale(1)' } } }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          disabled={addLoading || !addEmail}
          style={{ height: 48 }}
        >
          {addLoading ? 'Adding…' : 'Add'}
        </Button>
      </Box>
      {addError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {addError}
        </Typography>
      )}
      {domainWarning && (
        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'warning.main' }}>
          {domainWarning}
        </Typography>
      )}
    </Box>
  );
};

export default AddEmailRow;
